/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { showErrorToast, useAuth } from '@slr/shared-library';
import {
    GlobalErrorResponse,
    ResponseError
} from '@slr/user-service-external-sdk';
import { useMutation } from '@tanstack/react-query';
import {
    ACCOUNT_DELETION_BLOCKED_ERROR,
    APP_URL,
    DELETED_PROFILE_PATH,
    queryClient,
    queryKeys
} from 'configs';
import { useDataPrivacyApi } from 'services';

export const useDeleteUserAccount = () => {
  const { logout } = useAuth();

  const { deleteUserAccountStrict } = useDataPrivacyApi();

  return useMutation({
    mutationFn: deleteUserAccountStrict,
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.getAccountDeletionEligibility, userId]
      });
      logout(APP_URL + '/' + DELETED_PROFILE_PATH);
    },
    onError: async (error) => {
      if (error instanceof ResponseError) {
        try {
          const errorBody =
            (await error.response.json()) as GlobalErrorResponse;
          if (
            errorBody.uniqueExceptionType === ACCOUNT_DELETION_BLOCKED_ERROR
          ) {
            showErrorToast('toasts.deleteAccount.blocked');
            return;
          }
        } catch {
          // ignore parse errors
        }
      }
      showErrorToast('toasts.unexpected.error');
    }
  });
};
